/**
 * biome-ignore-all lint/style/useNamingConvention: these member names predate the rule, and the only reason
 * this file is touched is the header height below — renaming its internals belongs to a refactor of the
 * print paginators, not to a fix.
 */
import { RowGroupPaginator } from "@ext/markdown/elements/table/print/RowGroupPaginator";
import { RowPaginator } from "@ext/markdown/elements/table/print/RowPaginator";
import type { TablePaginatorInterface } from "@ext/markdown/elements/table/print/TablePaginator.types";
import { throwIfAborted } from "@ext/print/utils/pagination/abort";
import NodePaginator from "@ext/print/utils/pagination/NodePaginator";
import Paginator from "@ext/print/utils/pagination/Paginator";

export class TablePaginator
	extends NodePaginator<HTMLDivElement, HTMLTableSectionElement>
	implements TablePaginatorInterface
{
	private currentTableWrapper: HTMLDivElement;
	private repeatThead: HTMLTableSectionElement;
	private table: HTMLDivElement;
	private _rowIndex: number;

	constructor(tableWrapper: HTMLDivElement, parentPaginator: Paginator) {
		super(tableWrapper, parentPaginator);
		this.table = this.node.firstElementChild as HTMLDivElement;
		this.repeatThead = this.getRepeatableThead();
	}

	async paginateNode() {
		throwIfAborted(Paginator.controlInfo.signal);

		const allRows = this.collectBodyRows();

		const { table, tbody } = this.cloneTableShell();

		this.currentTableWrapper = this.node.cloneNode(false) as HTMLDivElement;
		this.parentPaginator.currentContainer.appendChild(this.currentTableWrapper);
		this.currentTableWrapper.appendChild(table);
		this.currentContainer = tbody;
		this.addDimension();

		for (this._rowIndex = 0; this._rowIndex < allRows.length; this._rowIndex++) {
			const row = allRows[this._rowIndex];
			const rowHeight = Paginator.paginationInfo.nodeDimension.get(row).height;

			if (RowGroupPaginator.hasRowspanCells(row)) {
				const groupIndices = this.getRowspanGroupIndices(allRows, this._rowIndex);
				const groupHeight = this.getGroupHeight(allRows, groupIndices);
				const usableHeight = this.getUsableHeight();

				if (groupHeight + Paginator.paginationInfo.accumulatedHeight.height > usableHeight)
					this.createPage(groupHeight);
				if (groupHeight + Paginator.paginationInfo.accumulatedHeight.height <= usableHeight) {
					for (const index of groupIndices) {
						const groupRow = allRows[index];
						const groupRowHeight = Paginator.paginationInfo.nodeDimension.get(groupRow).height;
						Paginator.paginationInfo.accumulatedHeight.height += groupRowHeight;
						this.currentContainer.appendChild(groupRow);
					}
					this._rowIndex += groupIndices.length - 1;
					continue;
				}

				const groupRows = groupIndices.map((idx) => allRows[idx]);
				const groupPaginator = new RowGroupPaginator(groupRows, this);
				await groupPaginator.paginateNode();
				this._rowIndex += groupIndices.length - 1;
				continue;
			}

			if (rowHeight + Paginator.paginationInfo.accumulatedHeight.height > this.getUsableHeight()) {
				this.createPage(rowHeight);
				if (rowHeight + Paginator.paginationInfo.accumulatedHeight.height > this.getUsableHeight()) {
					const rowPaginator = new RowPaginator(row, this);
					await rowPaginator.paginateNode();
					continue;
				}
			}
			Paginator.paginationInfo.accumulatedHeight.height += rowHeight;

			this.currentContainer.appendChild(row);
		}

		this.setMarginBottom();
		this.node.remove();
	}

	createPage(height?: number) {
		const isSplited = this.haveChildNodes();
		if (!isSplited) {
			this.currentTableWrapper.remove();
			if (
				this.parentPaginator.hasOnlyHeadingElements() &&
				this.getDimension().height + height > this.getUsableHeight()
			) {
				this.parentPaginator.currentContainer.appendChild(this.currentTableWrapper);
				return;
			}
		}
		this.cleanHeadingElementsIfNeed();

		if (isSplited) {
			const { table, tbody } = this.cloneTableShell();
			this.currentContainer = tbody;
			this.currentTableWrapper = this.node.cloneNode(false) as HTMLDivElement;
			this.currentTableWrapper.appendChild(table);
		}

		const parentPage = this.parentPaginator.createPage();
		parentPage.appendChild(this.currentTableWrapper);

		this.addDimension();
		this.setHeadings();
		return this.currentContainer;
	}

	getDimension() {
		const theadHeight = this.getTheadHeight();
		const addDimension = { ...this.nodeDimension };
		addDimension.height = theadHeight + addDimension.paddingH;
		return addDimension;
	}

	addDimension() {
		this.updateAccumulatedHeightDim(this.getDimension());
	}

	private getTheadHeight() {
		return parseFloat(this.repeatThead?.dataset._height || "0");
	}

	private cloneTableShell(withOutTableHeader?: boolean) {
		const newTable = this.table.cloneNode(false) as HTMLDivElement;
		for (const attr of Array.from(this.table.attributes)) newTable.setAttribute(attr.name, attr.value);

		const colgroup = this.table.querySelector("colgroup");
		if (colgroup) newTable.appendChild(colgroup.cloneNode(true));

		if (this.repeatThead && !withOutTableHeader) newTable.appendChild(this.repeatThead.cloneNode(true));

		const tbody = document.createElement("tbody");
		newTable.appendChild(tbody);
		return { table: newTable, tbody };
	}

	private getRepeatableThead() {
		const nodeDimension = Paginator.paginationInfo.nodeDimension;
		const thead = this.table.querySelector<HTMLTableSectionElement>("thead");
		if (thead) {
			// The synthesized headers below record their height because every page has to budget for the header
			// it repeats; a real <thead> needs the same and had none, so it was budgeted as zero. Article tables
			// never render one -- Table.tsx builds colgroup + tbody -- so the first tables to take this branch
			// are the Markdown ones inside an OpenAPI description, where <thead> is always there.
			const repeated = thead.cloneNode(true) as HTMLTableSectionElement;
			repeated.dataset._height = `${nodeDimension?.get(thead)?.height ?? 0}`;
			return repeated;
		}

		const headerAttr = (this.table.getAttribute("data-header") || "").toLowerCase().trim();

		if (headerAttr === "column") {
			return null;
		}

		if (headerAttr === "row" || headerAttr === "both") {
			const usablePageHeight = Paginator.printPageInfo.usablePageHeight;
			const rows = this.table.querySelectorAll<HTMLTableRowElement>("tbody tr");
			const firstBodyRow = rows[0];
			const isSimpleRow = Array.from(firstBodyRow.cells).every((cell) => !cell.rowSpan || cell.rowSpan === 1);
			const height = nodeDimension.get(firstBodyRow).height;

			if (firstBodyRow && isSimpleRow && rows.length > 1 && height < usablePageHeight / 2) {
				const newThead = document.createElement("thead");
				newThead.appendChild(firstBodyRow.cloneNode(true));
				newThead.dataset._synthesized = "from-first-row";
				newThead.dataset._height = `${nodeDimension?.get(firstBodyRow).height}`;
				return newThead;
			}
		}

		let headerRow = this.table.querySelector("tbody tr.repeat-header, tbody tr[data-repeat-header]");

		if (!headerRow) {
			const firstBodyRow = this.table.querySelector<HTMLTableRowElement>("tbody tr");

			if (firstBodyRow) {
				const allTh = Array.from(firstBodyRow.children).every((c) => c.tagName === "TH");
				if (allTh) headerRow = firstBodyRow;
			}
		}

		if (!headerRow) return null;
		const newThead = document.createElement("thead");
		newThead.dataset._synthesized = "1";
		newThead.appendChild(headerRow.cloneNode(true));
		newThead.dataset._height = `${nodeDimension?.get(headerRow as HTMLTableRowElement).height}`;
		return newThead;
	}

	private collectBodyRows() {
		const rows: HTMLTableRowElement[] = [];

		const headerRowFromFirst =
			this.repeatThead &&
			(this.repeatThead.dataset._synthesized === "from-first-row" ||
				this.repeatThead.dataset._synthesized === "1")
				? this.repeatThead.querySelector("tr")
				: null;

		let skippedFirst = false;
		this.node.querySelectorAll("tbody").forEach((tb) => {
			tb.querySelectorAll("tr").forEach((tr) => {
				if (headerRowFromFirst) {
					if (!skippedFirst) {
						skippedFirst = true;
						return;
					}
				}
				rows.push(tr);
			});
		});

		return rows;
	}
	private getRowspanGroupIndices(allRows: HTMLTableRowElement[], startIndex: number): number[] {
		const groupIndices = new Set<number>();
		const processedRows = new Set<number>();

		const collectGroup = (rowIndex: number) => {
			if (processedRows.has(rowIndex) || rowIndex >= allRows.length) return;
			processedRows.add(rowIndex);
			groupIndices.add(rowIndex);

			const row = allRows[rowIndex];

			for (const cell of row.cells) {
				if (cell.rowSpan > 1) {
					const endIndex = rowIndex + cell.rowSpan - 1;
					for (let i = rowIndex + 1; i <= endIndex && i < allRows.length; i++) {
						if (!processedRows.has(i)) {
							collectGroup(i);
						}
					}
				}
			}
		};

		collectGroup(startIndex);
		return Array.from(groupIndices).sort((a, b) => a - b);
	}

	private getGroupHeight(allRows: HTMLTableRowElement[], groupIndices: number[]): number {
		let totalHeight = 0;
		for (const index of groupIndices) {
			totalHeight += Paginator.paginationInfo.nodeDimension.get(allRows[index]).height;
		}
		return totalHeight;
	}
}
